import { Heading, Chip, Text } from '@weave/design-system';
import { theme } from '@weave/theme-original';
import { ReactNode } from 'react';
import { AnnouncementData, SimpleAnnouncementData } from './types';
import { parseInlineMarkdown } from '../../utils/parseInlineMarkdown';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import localizedFormat from 'dayjs/plugin/localizedFormat';

// Extend dayjs with plugins
dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(localizedFormat);

export const AnnouncementCard = ({
  id,
  title,
  isPublished,
  message,
  createdBy,
  createdOn,
  type,
  isSelected,
  onClick,
}: {
  id: string;
  title: string;
  isPublished: boolean;
  message: string;
  createdBy: string;
  createdOn: string;
  type: string;
  isSelected?: boolean;
  onClick?: () => void;
}) => {
  return (
    <div
      style={{
        outline: isSelected ? `2px solid ${theme.colors.weaveBlue}` : '1px solid #e1e5e9',
        padding: theme.spacing(1),
        borderRadius: theme.borderRadius.small,
        background: theme.colors.white,
        display: 'grid',
        gridTemplateAreas:
          '"title title status" "message message message" "meta meta meta"',
        gridTemplateRows: 'auto 1fr auto',
        cursor: 'pointer',
        transition: 'border-color 0.2s ease',
        gap: theme.spacing(1),
      }}
      onClick={onClick}
    >
      <Heading level={3} css={{ gridArea: 'title', margin: 0 }}>
        {title}
      </Heading>
      {isPublished ? (
        <Chip variant="success" css={{ gridArea: 'status', justifySelf: 'end' }}>
          Published
        </Chip>
      ) : (
        <Chip variant="warn" css={{ gridArea: 'status', justifySelf: 'end' }}>
          Unpublished
        </Chip>
      )}
      <Text css={{ gridArea: 'message', margin: 0 }}>{parseInlineMarkdown(message)}</Text>
      <div
        css={{
          gridArea: 'meta',
          display: 'grid',
          gap: theme.spacing(1),
          gridTemplateColumns: 'repeat(2, 1fr)',
        }}
      >
        {/* <MetaItem label="Type" value={type} /> */}
        <MetaItem label="Created by" value={createdBy} />
        <MetaItem label="Created On" value={formatTimestamp(createdOn)} />
      </div>
    </div>
  );
};

const MetaItem = ({ label, value }: { label: string; value: string | ReactNode }) => {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateRows: 'max-content max-content',
      }}
    >
      <Text as="span" size="small" color="light">
        {label}
      </Text>
      {typeof value === 'string' ? (
        <Text as="span" size="medium">
          {value}
        </Text>
      ) : (
        value
      )}
    </div>
  );
};

export const AnnouncementAlertList = ({
  announcements,
  selectedAnnouncementId,
  onAnnouncementSelect,
}: {
  announcements?: AnnouncementData[];
  selectedAnnouncementId: string;
  onAnnouncementSelect: (id: string) => void;
}) => {
  if (!announcements) {
    return <div>Loading...</div>;
  }

  // Get the active version of each announcement for display in the list
  const activeVersions = announcements.map((announcement) => {
    const activeVersion =
      announcement.versions.find((v) => v.version === announcement.activeVersionId) ??
      announcement.versions[0];
    return {
      ...activeVersion,
      id: announcement.id,
      isPublished: announcement.isPublished,
    };
  });

  return (
    <div
      style={{
        background: theme.colors.gray100,
        padding: theme.spacing(2),
        display: 'grid',
        gap: theme.spacing(2),
        gridTemplateRows: 'repeat(auto-fill, minmax(150px, 200px))',
        borderRadius: theme.borderRadius.medium,
        overflowY: 'auto',
      }}
    >
      {activeVersions.map((data) => (
        <AnnouncementCard
          key={data.id}
          id={data.id}
          title={data.title}
          isPublished={data.isPublished}
          message={data.message}
          createdBy={data.createdBy}
          createdOn={data.createdOn}
          type={data.type}
          isSelected={selectedAnnouncementId === data.id}
          onClick={() => onAnnouncementSelect(data.id)}
        />
      ))}
    </div>
  );
};

export const SimpleAnnouncementAlertList = ({
  announcements,
  selectedAnnouncementId,
  onAnnouncementSelect,
}: {
  announcements?: SimpleAnnouncementData[];
  selectedAnnouncementId: string;
  onAnnouncementSelect: (id: string) => void;
}) => {
  if (!announcements) {
    return <div>Loading...</div>;
  }

  return (
    <div
      style={{
        background: theme.colors.gray100,
        padding: theme.spacing(2),
        display: 'grid',
        gap: theme.spacing(2),
        gridTemplateRows: 'repeat(auto-fill, minmax(150px, 200px))',
        borderRadius: theme.borderRadius.medium,
        overflowY: 'auto',
      }}
    >
      {announcements.map((data) => (
        <AnnouncementCard
          key={data.id}
          id={data.id}
          title={data.title}
          isPublished={data.isPublished}
          message={data.message}
          createdBy={data.createdBy}
          createdOn={data.createdOn}
          type={data.type}
          isSelected={selectedAnnouncementId === data.id}
          onClick={() => onAnnouncementSelect(data.id)}
        />
      ))}
    </div>
  );
};

// Helper function to format timestamps to human-readable format
const formatTimestamp = (timestamp: string): string => {
  return dayjs(timestamp).format('MMM D, YYYY h:mm:ss A z');
};
